import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

function RelayEditorPage() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [lastChapter, setLastChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  useEffect(() => {
    fetchStoryInfo();
  }, [storyId]);

  const fetchStoryInfo = async () => {
    try {
      const [storyRes, chaptersRes] = await Promise.all([
        fetch(`/api/stories/${storyId}`),
        fetch(`/api/stories/${storyId}/chapters?limit=1`)
      ]);
      
      const storyData = await storyRes.json();
      const chaptersData = await chaptersRes.json();
      
      if (storyData.story) setStory(storyData.story);
      if (chaptersData.chapters && chaptersData.chapters.length > 0) {
        setLastChapter(chaptersData.chapters[0]);
      }
    } catch (error) {
      console.error('获取信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const token = localStorage.getItem('token');
    if (!token) {
      alert('请先登录');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`/api/stories/${storyId}/chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok || data.message) {
        alert('✅ 章节发布成功！');
        navigate(`/stories/${storyId}`);
      } else {
        alert('❌ ' + (data.error || '发布失败'));
      }
    } catch (error) {
      alert('❌ 网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">加载中...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 顶部信息 */}
      <div className="mb-6">
        <Link to={`/stories/${storyId}`} className="text-gray-400 hover:text-white text-sm">
          ← 返回故事
        </Link>
        <h1 className="text-2xl font-bold mt-2">
          ✍️ 接力创作：{story?.title}
        </h1>
      </div>

      {/* 前一章内容参考 */}
      {lastChapter && (
        <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-6 border border-purple-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">📖 上一章内容参考</h3>
            <span className="text-sm text-gray-400">
              第{lastChapter.chapter_number}章 - {lastChapter.author_name}
            </span>
          </div>
          <div className="bg-gray-900/50 rounded p-4 max-h-96 overflow-y-auto">
            <h4 className="font-bold mb-2">{lastChapter.title}</h4>
            <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
              {lastChapter.content}
            </p>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 提示：请基于上一章的内容继续创作，保持故事连贯性
          </p>
        </div>
      )}

      {/* 创作表单 */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 章节标题 */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">
            章节标题 *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="给这一章起个名字"
          />
        </div>

        {/* 章节内容 */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">
            章节内容 *
          </label>
          <textarea
            required
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            rows="20"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono leading-relaxed"
            placeholder="开始你的创作..."
          />
          <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
            <span>💡 支持 Markdown 格式</span>
            <span>{formData.content.length} 字</span>
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={submitting || formData.content.length < 100}
            className="flex-1 py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? '发布中...' : '🚀 发布章节'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/stories/${storyId}`)}
            className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium"
          >
            取消
          </button>
        </div>

        {formData.content.length < 100 && (
          <p className="text-sm text-orange-400">
            ⚠️ 章节内容至少需要 100 字，当前 {formData.content.length} 字
          </p>
        )}
      </form>

      {/* 接龙提示 */}
      <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="font-bold mb-4">📜 接龙创作指南</h3>
        <div className="space-y-3 text-sm text-gray-400">
          <div className="flex items-start space-x-3">
            <span className="text-primary-400">✓</span>
            <p>阅读上一章内容，理解当前剧情发展</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-primary-400">✓</span>
            <p>保持人物性格和世界观的一致性</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-primary-400">✓</span>
            <p>可以引入新角色或新情节，但不要颠覆已有设定</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-primary-400">✓</span>
            <p>每章建议字数：1000-5000 字</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-primary-400">✓</span>
            <p>留下适当的悬念，让后续创作更有空间</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RelayEditorPage;
